import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
// Removed filter Select imports
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Search, Plus, Eye, Edit, Trash2 } from "lucide-react";
import { usePageStore } from "@/stores/pageStore";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuthorStore } from "@/stores/authorStore";
import PermissionGuard from "@/components/PermissionGuard";
import { useDebounce } from "@/hooks/useDebounce";
import { Page } from "@/types/page";
import { ROUTES } from "@/constants/routes";
// import { showToast } from "@/lib/toast";

// Modal components

import { ModalDeletePage } from "@/components/page/ModalDeletePage";
import { ModalViewPage } from "@/components/page/ModalViewPage";

export default function PagePage() {
  const navigate = useNavigate();
  const {
    pages,
    pagination,
    isLoading,
    fetchPages,
    setCurrentPage,
    setSelectedPage,
    clearSelectedPage,
  } = usePageStore();

  const { canRead } = usePermissions();
  const { authors, fetchAuthors } = useAuthorStore();

  // Search and filters
  const [searchTerm, setSearchTerm] = useState("");
  // Giữ lại filter tác giả
  const [authorFilter, setAuthorFilter] = useState<string>("_all");

  const debouncedSearch = useDebounce(searchTerm, 500);
  const [, startTransition] = useTransition();
  const deferredPages = useDeferredValue(pages);
  const searchRafRef = useRef<number | null>(null);

  // Modal states
  // Edit modal state removed
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [, setSelectedPageLocal] = useState<Page | null>(null);

  // Fetch pages on mount and when filters change
  useEffect(() => {
    fetchAuthors({ page: 1, limit: 1000 });
  }, [fetchAuthors]);

  // Build query params memoized
  const queryParams = useMemo(
    () =>
      ({
        page: pagination.page,
        limit: pagination.limit,
        search: debouncedSearch || undefined,
        author: authorFilter === "_all" ? undefined : authorFilter,
      } as const),
    [debouncedSearch, pagination.page, pagination.limit, authorFilter]
  );

  // Fetch pages on mount and when filters/page change
  useEffect(() => {
    startTransition(() => {
      fetchPages({ ...queryParams });
    });
  }, [queryParams, fetchPages]);

  // Handle search
  const handleSearch = useCallback(() => {
    setCurrentPage(1);
  }, [setCurrentPage]);

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  useEffect(() => {
    return () => {
      if (searchRafRef.current) {
        cancelAnimationFrame(searchRafRef.current);
        searchRafRef.current = null;
      }
    };
  }, []);

  // Handle pagination
  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
    },
    [setCurrentPage]
  );

  const pagesList = useMemo(() => {
    if (isLoading) {
      return (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-2">Đang tải...</p>
        </div>
      );
    }
    if (deferredPages.length === 0) {
      return (
        <div className="text-center py-8">
          <div className="text-gray-400 text-lg mb-2">Không có trang nào</div>
          <div className="text-gray-500">
            Thử thay đổi bộ lọc hoặc tạo trang mới
          </div>
        </div>
      );
    }
    return (
      <>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/80 dark:bg-gray-800/80 backdrop-blur supports-[backdrop-filter]:bg-gray-50/60 dark:supports-[backdrop-filter]:bg-gray-800/60">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Đường dẫn
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Tiêu đề
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Mô tả
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Tác giả
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Ngày xuất bản
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Trạng thái
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Hành động
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
              {deferredPages.map((page) => {
                const statusLabel = getStatusLabel(page.visibility);
                const statusVariant = getStatusBadgeVariant(page.visibility);
                const description = page.pageDescription
                  ? page.pageDescription
                  : page.content.replace(/<[^>]*>/g, "").substring(0, 150) +
                    "...";
                const publishLabel = page.publishDate
                  ? new Date(page.publishDate).toLocaleDateString("vi-VN", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    })
                  : "-";
                return (
                  <tr
                    key={page._id}
                    className="transition-all hover:bg-gray-50/60 dark:hover:bg-gray-800/60"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 truncate max-w-[240px]">
                      <Link to={`/pages/${page._id}/edit`}> /{page.slug}</Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white truncate max-w-[260px]">
                      {page.title || "-"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 truncate max-w-[320px]">
                      {description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {page.author?.name || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {publishLabel}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge
                        variant={statusVariant}
                        className={
                          page.visibility === "private"
                            ? "bg-red-200 text-red-700"
                            : "bg-blue-200 text-blue-700"
                        }
                      >
                        {statusLabel}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <PermissionGuard subject="page" action="read">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleView(page)}
                            className="cursor-pointer"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </PermissionGuard>
                        <PermissionGuard subject="page" action="update">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(page)}
                            className="cursor-pointer"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </PermissionGuard>
                        <PermissionGuard subject="page" action="delete">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(page)}
                            className="cursor-pointer text-red-600 hover:text-red-700 hover:border-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </PermissionGuard>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <Button
              variant="outline"
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="cursor-pointer"
            >
              Trước
            </Button>

            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(
              (page) => (
                <Button
                  key={page}
                  variant={page === pagination.page ? "default" : "outline"}
                  onClick={() => handlePageChange(page)}
                  className="cursor-pointer"
                >
                  {page}
                </Button>
              )
            )}

            <Button
              variant="outline"
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
              className="cursor-pointer"
            >
              Sau
            </Button>
          </div>
        )}
      </>
    );
  }, [deferredPages, isLoading, pagination, handlePageChange]);

  // Handle actions
  const handleView = (page: Page) => {
    setSelectedPageLocal(page);
    setSelectedPage(page);
    setIsViewModalOpen(true);
  };

  const handleEdit = (page: Page) => {
    navigate(`/pages/${page._id}/edit`);
  };

  const handleDelete = (page: Page) => {
    setSelectedPageLocal(page);
    setSelectedPage(page);
    setIsDeleteModalOpen(true);
  };

  // Hành động đổi trạng thái/hiển thị hiện không sử dụng trong UI; có thể bật lại khi cần

  // Format date
  // Removed unused formatDate helper

  // Get status badge variant
  function getStatusBadgeVariant(status: string) {
    switch (status) {
      case "public":
        return "default";
      case "private":
        return "secondary";
      default:
        return "secondary";
    }
  }

  // Get status label
  function getStatusLabel(status: string) {
    switch (status) {
      case "public":
        return "Công khai";
      case "private":
        return "Riêng tư";

      default:
        return status;
    }
  }

  // Get visibility label
  // Removed unused getVisibilityLabel helper

  // Check if user can access page
  if (!canRead("page")) {
    return (
      <DashboardLayout>
        <div className="p-10 text-center">
          <div className="text-red-600 text-lg font-medium mb-2">
            Không có quyền truy cập
          </div>
          <div className="text-gray-500">
            Bạn không có quyền truy cập trang quản lý trang
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600">
                Quản lý trang
              </h1>
              <div className="mt-2 flex items-center gap-2">
                <p className="text-gray-500 dark:text-gray-400">
                  Quản lý và xuất bản các trang trên website
                </p>
                <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200">
                  {pagination.total} trang
                </span>
              </div>
            </div>
            <PermissionGuard subject="page" action="create">
              <Button
                onClick={() => navigate(ROUTES.CREATE_PAGE)}
                className="bg-blue-600 hover:bg-blue-700 cursor-pointer"
              >
                <Plus className="w-4 h-4 mr-2" />
                Thêm trang mới
              </Button>
            </PermissionGuard>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Tìm kiếm</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Tìm kiếm
                </label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Tìm theo tiêu đề, nội dung..."
                    value={searchTerm}
                    onChange={handleSearchInputChange}
                    className="flex-1"
                    type="text"
                  />
                  <Button onClick={handleSearch} className="cursor-pointer">
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Tác giả
                </label>
                <Select
                  value={authorFilter}
                  onValueChange={(v) => setAuthorFilter(v)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Tất cả tác giả" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Tất cả tác giả</SelectItem>
                    {authors.map((a) => (
                      <SelectItem key={a._id} value={a._id}>
                        {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pages List */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                Danh sách trang
                <Badge variant="secondary" className="ml-2">
                  {pagination.total} trang
                </Badge>
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>{pagesList}</CardContent>
        </Card>

        <ModalDeletePage
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            clearSelectedPage();
          }}
          onComplete={() => {
            setIsDeleteModalOpen(false);
            clearSelectedPage();
            // Refresh pages
            fetchPages({
              page: pagination.page,
              limit: pagination.limit,
              search: debouncedSearch || undefined,
              author: authorFilter === "_all" ? undefined : authorFilter,
            });
          }}
        />

        <ModalViewPage
          isOpen={isViewModalOpen}
          onClose={() => {
            setIsViewModalOpen(false);
            clearSelectedPage();
          }}
        />
      </div>
    </DashboardLayout>
  );
}
